import {
  SpecialistbevisAnsokanFormStepOneModel,
  FolkbokforingsAdress,
  SpecialistbevisAnsokan,
  SpecialistbevisAnsokanPayload,
  AnsokanFormTexts,
  LegitimationsDetalj,
  SpecialistbevisDetalj,
} from '../../../models/specialistbevisansokan.model';
import { FormFieldProps } from 'sos-common/js/commonTypes/formField.types';
import { ResourceItem } from 'sos-common/js/commonTypes/resourceItem';
import { TextInputProps } from 'sos-common/js/components/FormControls/TextInput';
import { SelectBoxProps } from 'sos-common/js/components/FormControls/SelectBox';
import { IFileUploadProps } from 'sos-common/js/components/Fileupload/Fileupload.types';
import { CheckBoxProps } from 'sos-common/js/components/FormControls/CheckBox';
import { SpecialistbevisFieldMap } from './SpecialistbevisFieldMap';
import { EducationLocation } from '../../../models/ansokan.model';

export interface ISpecAnsokanFormProps {
  model: SpecialistbevisAnsokanFormStepOneModel;
  folkbokforing: FolkbokforingsAdress;
  ansokan: SpecialistbevisAnsokan;
  stepTwoPageLink: string;
  formTexts: AnsokanFormTexts;
  deleteLink: string;
  myPage: string;
  translations: ResourceItem[];
  modalTranslations: ResourceItem[];
  currentLanguage: string;
  isOutsideEuEes: boolean;
  specialistBevisTypeOtherId: string;
  educationLocation: EducationLocation;
  specialistBevisTyp: SpecialistbevisTyp;
}

export interface MultiSelectItem {
  key: string;
  value: string;
  isSelectedByDefault: boolean;
}

export const LEGITIMATION_MIN_ENTRIES = 1;
export const LEGITIMATION_MAX_ENTRIES = 3;
export const EARLIER_SPECIALIST_MIN_ENTRIES = 0;
export const EARLIER_SPECIALIST_MAX_ENTRIES = 3;

export interface ISpecAnsokanFormState {
  fieldMap: SpecialistbevisFieldMap;
  payload: SpecialistbevisAnsokanPayload;
  showSpinner: boolean;
  isLoading: boolean;
  isSaveErrorModalOpen: boolean;
  showAddressInfo: boolean;
  certInOtherCountry?: boolean;
  showOvrigSpecialistbevisTyp?: boolean;
  legitimationEntries: LegitimationsDetalj[];
  earlierSpecialistEntries: SpecialistbevisDetalj[];
}

export type SpecialistbevisAnsokanFieldMap = Map<
  SpecialistbevisAnsokanFormFieldName | string,
  SpecialistbevisAnsokanField
>;
export type SpecialistbevisAnsokanField =
  | FormFieldProps<any>
  | TextInputProps
  | SelectBoxProps
  | IFileUploadProps
  | CheckBoxProps;

export enum SpecialistbevisAnsokanFormFieldName {
  Email = 'Epost',
  ConfirmEmail = 'BekraftaEpost',
  TermsAndConditions = 'AccepteratVillkor',
  CheckboxValue = 'CheckboxValue',
  UtbildningsLand = 'UtbildningslandId',
  SpecialistbevisType = 'TypAvSpecialistKompetensId',
  OvrigSpecialistbevisTyp = 'OvrigSpecialistKompetens',
  HasCertInOtherCountry = 'SpecBehorighetsbevisFranAnnatLand',
  OtherCertCountries = 'LanderForSpecialistBevis',
  WorkPlace = 'Arbetsplats',
  ExaminationDate = 'ExamensDatum',
  GeneralServiceCertificateDate = 'BasTjanstgoringsDatum',
  GeneralDentalPracticeCountry = 'AllmanTandlakarpraktikLandId',
  GeneralDentalPracticeDate = 'AllmanTandlakarpraktikDatum',
  LegitimationCountry = 'LegitimationCountry',
  LegitimationDate = 'LegitimationDate',
  Legitimation = 'Legitimation',
  EarlierSpecialstCompetence = 'TidigareSpecialistkompetens',
  FileUpload1 = 'Fileupload1Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload2 = 'Fileupload2Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload3 = 'Fileupload3Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload4 = 'Fileupload4Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload5 = 'Fileupload5Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload6 = 'Fileupload6Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload7 = 'Fileupload7Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload8 = 'Fileupload8Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload9 = 'Fileupload9Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload10 = 'Fileupload10Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload11 = 'Fileupload11Files', //NOTE: If changed, server  FileTag enum name has to change too.
  FileUpload12 = 'Fileupload12Files', //NOTE: If changed, server  FileTag enum name has to change too.
}

export enum SpecialistbevisYrke {
  Lakare = '100009',
  Sjukskoterska = '100019',
  Tandlakare = '100021',
  Sjukhusfysiker = '100018',
}

export const YRKE_LIST = [
  SpecialistbevisYrke.Lakare,
  SpecialistbevisYrke.Sjukskoterska,
  SpecialistbevisYrke.Tandlakare,
  SpecialistbevisYrke.Sjukhusfysiker,
];

export const getLegitimationCountryFieldName = (index: number): SpecialistbevisAnsokanFormFieldName | string =>
  `${SpecialistbevisAnsokanFormFieldName.LegitimationCountry}-${index}`;

export const getLegitimationDateFieldName = (index: number): SpecialistbevisAnsokanFormFieldName | string =>
  `${SpecialistbevisAnsokanFormFieldName.LegitimationDate}-${index}`;

export const createEmptyLegitimationEntry = (): LegitimationsDetalj => ({
  LandId: null,
  Datum: null,
});

export const getEarlierSpecialistSpecialityFieldName = (index: number): SpecialistbevisAnsokanFormFieldName | string =>
  `${SpecialistbevisAnsokanFormFieldName.EarlierSpecialstCompetence}-speciality-${index}`;

export const getEarlierSpecialistCountryFieldName = (index: number): SpecialistbevisAnsokanFormFieldName | string =>
  `${SpecialistbevisAnsokanFormFieldName.EarlierSpecialstCompetence}-country-${index}`;

export const getEarlierSpecialistDateFieldName = (index: number): SpecialistbevisAnsokanFormFieldName | string =>
  `${SpecialistbevisAnsokanFormFieldName.EarlierSpecialstCompetence}-date-${index}`;

export const createEmptyEarlierSpecialistEntry = (): SpecialistbevisDetalj => ({
  Specialitet: null,
  LandId: null,
  Datum: null,
});

export enum SpecialistbevisTyp {
  None = 0,
  LAKARE_HSLF_2021_8 = 1,
  LAKARE_SOSFS_2015_8 = 2,
  TANDLAKARE = 3,
  SJUKHUSFYSIKER = 4,
  INTYG_BASTJANSTGORING = 5,
}
